import { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import Swal from "sweetalert2";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import { useAuth } from "contexts/AuthContext";
import { API_BASE, API_HOST } from "config/api";

const DAYS_OPTIONS = [7, 14, 30, 60, 90];

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long" });
};

function BirthdayAvatarCell({ value, row }) {
  const photo = row.original.photo;
  return (
    <Box display="flex" alignItems="center" gap={1.5}>
      <Avatar
        src={photo ? `${API_HOST}/storage/${photo}` : undefined}
        sx={{ width: 34, height: 34, fontSize: 14, bgcolor: "primary.main" }}
      >
        {value?.charAt(0)}
      </Avatar>
      <Box>
        <Typography variant="button" fontWeight={600} display="block">
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.original.phone || "No phone"}
        </Typography>
      </Box>
    </Box>
  );
}
BirthdayAvatarCell.propTypes = {
  value: require("prop-types").string,
  row: require("prop-types").shape({ original: require("prop-types").object }).isRequired,
};

function DaysChip({ value }) {
  const color = value === 0 ? "error" : value <= 3 ? "warning" : value <= 7 ? "info" : "default";
  const label = value === 0 ? "Today! 🎂" : `${value} day${value === 1 ? "" : "s"}`;
  return <Chip size="small" label={label} color={color} sx={{ fontWeight: 600 }} />;
}
DaysChip.propTypes = { value: require("prop-types").number };

function SendActions({ row }) {
  const { phone, email, first_name, surname, age_turning } = row.original;
  const name = `${first_name} ${surname}`;
  const wish = `Happy ${ordinal(
    age_turning
  )} Birthday, ${first_name}! Wishing you a wonderful day. 🎂`;

  const onSms = () => {
    if (!phone) {
      Swal.fire("No phone number", `${name} has no phone number on file.`, "warning");
      return;
    }
    Swal.fire({
      title: `Send Birthday SMS to ${first_name}?`,
      html: `<strong>To:</strong> ${phone}<br/><br/><em>${wish}</em>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Send SMS",
    }).then((r) => {
      if (r.isConfirmed) {
        const token = localStorage.getItem("authToken");
        fetch(`${API_BASE}/v3/sms/send`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ recipient: phone, message: wish, type: "birthday_anniversary" }),
        })
          .then((res) => res.json())
          .then(() =>
            Swal.fire({
              title: "Sent!",
              text: `Birthday SMS sent to ${name}.`,
              icon: "success",
              timer: 2000,
            })
          )
          .catch(() => Swal.fire("Error", "Failed to send SMS.", "error"));
      }
    });
  };

  const onWhatsApp = () => {
    const cleaned = phone?.replace(/\D/g, "") || "";
    if (!cleaned) {
      Swal.fire("No phone number", `${name} has no phone number on file.`, "warning");
      return;
    }
    window.open(`https://wa.me/${cleaned}?text=${encodeURIComponent(wish)}`, "_blank");
  };

  const onEmail = () => {
    if (!email) {
      Swal.fire("No email", `${name} has no email address on file.`, "warning");
      return;
    }
    window.open(
      `mailto:${email}?subject=Happy%20Birthday%20${encodeURIComponent(
        first_name
      )}!&body=${encodeURIComponent(wish)}`,
      "_blank"
    );
  };

  return (
    <Box display="flex" gap={0.75} justifyContent="center">
      <Button size="small" variant="outlined" color="success" onClick={onSms}>
        SMS
      </Button>
      <Button size="small" variant="outlined" color="primary" onClick={onWhatsApp}>
        WhatsApp
      </Button>
      <Button size="small" variant="outlined" color="info" onClick={onEmail}>
        Email
      </Button>
    </Box>
  );
}
SendActions.propTypes = {
  row: require("prop-types").shape({
    original: require("prop-types").object,
  }).isRequired,
};

function SerialCell({ value }) {
  return (
    <Typography variant="caption" color="text.secondary" fontWeight={600}>
      {value}
    </Typography>
  );
}
SerialCell.propTypes = { value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };

function BirthdayDateCell({ value }) {
  return (
    <Typography variant="caption" fontWeight={600}>
      {formatDate(value)}
    </Typography>
  );
}
BirthdayDateCell.propTypes = { value: PropTypes.string };

function DaysUntilCell({ value }) {
  return <DaysChip value={value} />;
}
DaysUntilCell.propTypes = { value: PropTypes.number };

function AgeTurningCell({ value }) {
  return (
    <Typography variant="caption" fontWeight={600}>
      {ordinal(value)}
    </Typography>
  );
}
AgeTurningCell.propTypes = { value: PropTypes.number };

const COLUMNS = [
  { Header: "#", accessor: "serial", align: "center", width: "4%", Cell: SerialCell },
  { Header: "Member", accessor: "name", align: "left", width: "28%", Cell: BirthdayAvatarCell },
  {
    Header: "Birthday",
    accessor: "next_birthday",
    align: "center",
    width: "15%",
    Cell: BirthdayDateCell,
  },
  { Header: "In", accessor: "days_until", align: "center", width: "12%", Cell: DaysUntilCell },
  {
    Header: "Turning",
    accessor: "age_turning",
    align: "center",
    width: "10%",
    Cell: AgeTurningCell,
  },
  {
    Header: "Actions",
    accessor: "actions",
    align: "center",
    width: "31%",
    Cell: (props) => <SendActions {...props} />,
  },
];

export default function Birthdays() {
  const { church } = useAuth();
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!church?.id) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await window.fetch(`${API_BASE}/members/birthdays?days=${days}`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      const data = json.data || [];
      setRows(
        data.map((m, i) => ({
          serial: i + 1,
          ...m,
          name: `${m.first_name} ${m.surname}`,
        }))
      );
    } catch {
      Swal.fire("Error", "Failed to load upcoming birthdays.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [church?.id, days]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const todayCount = rows.filter((r) => r.days_until === 0).length;

  return (
    <Box>
      <PageHeader
        title="Upcoming Birthdays"
        subtitle="Send birthday wishes via SMS, WhatsApp, or Email."
        actions={
          <TextField
            select
            size="small"
            label="Show next"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            sx={{ width: 150 }}
          >
            {DAYS_OPTIONS.map((d) => (
              <MenuItem key={d} value={d}>
                {d} days
              </MenuItem>
            ))}
          </TextField>
        }
      />

      {todayCount > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            background: "linear-gradient(135deg,#e3f2fd,#bbdefb)",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Icon sx={{ color: "primary.main", fontSize: 28 }}>cake</Icon>
          <Typography variant="subtitle1" fontWeight={700} color="primary.main">
            {todayCount === 1
              ? "1 member celebrates their birthday today!"
              : `${todayCount} members celebrate their birthdays today!`}
          </Typography>
        </Box>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2.5, textAlign: "center" }}>
            <Icon sx={{ fontSize: 36, color: "error.main" }}>cake</Icon>
            <Typography variant="h4" fontWeight={800} color="error.main">
              {rows.filter((r) => r.days_until === 0).length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Today
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2.5, textAlign: "center" }}>
            <Icon sx={{ fontSize: 36, color: "warning.main" }}>upcoming</Icon>
            <Typography variant="h4" fontWeight={800} color="warning.main">
              {rows.filter((r) => r.days_until > 0 && r.days_until <= 7).length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This week
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2.5, textAlign: "center" }}>
            <Icon sx={{ fontSize: 36, color: "info.main" }}>event_note</Icon>
            <Typography variant="h4" fontWeight={800} color="info.main">
              {rows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Next {days} days
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {isLoading ? (
        <Typography sx={{ py: 6 }}>Loading birthdays...</Typography>
      ) : (
        <Card>
          <DataTable
            table={{ columns: COLUMNS, rows }}
            isSorted={false}
            entriesPerPage
            showTotalEntries
            noEndBorder
            canSearch
            searchLabel="Search members..."
          />
        </Card>
      )}
    </Box>
  );
}
